import { useBulkUpdateDraftPostStatus } from '../../hooks/admin/useAdminMutations'
import { useAdminDraftPosts } from '../../hooks/admin/useAdminQueries'
import { useAdminStore, ITEMS_PER_PAGE } from '../../stores/adminStore'
import { DraftPostViewMode, DraftPostStatus, AdminDraftPostDto } from '../../types/admin-types'
import React, { useEffect, useState } from 'react'

interface GroupedPosts {
    [key: string]: AdminDraftPostDto[]
}

const DraftPostList: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [selectedBulkStatus, setSelectedBulkStatus] = useState<DraftPostStatus | ''>('')

    // Select state slices individually from Zustand
    const selectedUserId = useAdminStore((state) => state.selectedUserId)
    const selectedStatus = useAdminStore((state) => state.selectedStatus)
    const viewMode = useAdminStore((state) => state.viewMode)
    const selectedDraftPostId = useAdminStore((state) => state.selectedDraftPostId)
    const selectedDraftPostIds = useAdminStore((state) => state.selectedDraftPostIds)
    const setSelectedDraftPostId = useAdminStore((state) => state.setSelectedDraftPostId)
    const setViewMode = useAdminStore((state) => state.setViewMode)
    const setSelectedStatus = useAdminStore((state) => state.setSelectedStatus)
    const toggleDraftPostSelection = useAdminStore((state) => state.toggleDraftPostSelection)
    const clearBulkSelection = useAdminStore((state) => state.clearBulkSelection)

    const {
        data: postsData,
        isLoading: postsLoading,
        isFetching: postsFetching,
        isError: postsIsError,
        error: postsError,
    } = useAdminDraftPosts({
        user_id: selectedUserId || undefined,
        status: selectedStatus || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
    })

    const {
        mutate: bulkUpdateMutate,
        isPending: bulkUpdateLoading,
        isError: bulkUpdateIsError,
        error: bulkUpdateError,
    } = useBulkUpdateDraftPostStatus()

    const ideaStatusFilters = [
        { value: DraftPostStatus.IDEA_UNDER_REVIEW, label: 'To Review' },
        { value: DraftPostStatus.IDEA_REJECTED, label: 'Rejected' },
        { value: DraftPostStatus.PENDING_RETRY, label: 'Flagged for Retry' },
    ]

    const postStatusFilters = [
        {
            value: 'pending',
            label: 'Pending',
            isSpecial: true,
            statuses: [
                DraftPostStatus.IDEA_APPROVED_WITH_RESEARCH,
                DraftPostStatus.IDEA_APPROVED_WITHOUT_RESEARCH,
            ],
        },
        { value: DraftPostStatus.POST_UNDER_REVIEW, label: 'Ready for Review' },
        { value: DraftPostStatus.POST_APPROVED, label: 'Approved' },
        { value: DraftPostStatus.POST_REJECTED, label: 'Rejected' },
    ]
    const feedbackStatusFilters = [
        { value: DraftPostStatus.IDEA_REJECTED, label: 'Idea Rejected' },
        { value: DraftPostStatus.POST_APPROVED, label: 'Post Approved' },
        { value: DraftPostStatus.POST_REJECTED, label: 'Post Rejected' },
    ]

    useEffect(() => {
        if (selectedStatus === null) {
            if (viewMode === DraftPostViewMode.IDEAS) {
                setSelectedStatus(DraftPostStatus.IDEA_UNDER_REVIEW)
            } else {
                setSelectedStatus(DraftPostStatus.POST_UNDER_REVIEW)
            }
        }
        setCurrentPage(1)
        clearBulkSelection()
    }, [viewMode, selectedStatus, setSelectedStatus, clearBulkSelection])

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const totalDraftPosts = postsData?.pagination.total || 0
    const totalPages = Math.ceil(totalDraftPosts / ITEMS_PER_PAGE)

    // Group posts by their source (meeting / direct conversation, etc.)
    const groupPostsBySource = (posts: AdminDraftPostDto[]): GroupedPosts => {
        return posts.reduce((groups: GroupedPosts, post: AdminDraftPostDto) => {
            if (!post.source_info) {
                const groupKey = 'Other Content'
                if (!groups[groupKey]) {
                    groups[groupKey] = []
                }
                groups[groupKey].push(post)
                return groups
            }
            const src = post.source_info
            const groupKey = (src.title || 'Untitled Source') + ' - ' + src.created_at
            if (!groups[groupKey]) {
                groups[groupKey] = []
            }
            groups[groupKey].push(post)
            return groups
        }, {})
    }

    const handleSelectFilter = (filter: any) => {
        clearBulkSelection()
        setCurrentPage(1)
        if (filter.isSpecial && filter.statuses) {
            setSelectedStatus(filter.statuses[0])
        } else {
            setSelectedStatus(filter.value)
        }
    }

    const isFilterSelected = (filter: any) => {
        if (filter.isSpecial && filter.statuses) {
            return filter.statuses.includes(selectedStatus)
        }
        return selectedStatus === filter.value
    }

    const getStatusLabel = (status: string): string => {
        switch (status) {
            case DraftPostStatus.IDEA_APPROVED_WITH_RESEARCH:
            case DraftPostStatus.IDEA_APPROVED_WITHOUT_RESEARCH:
                return 'Pending'
            case DraftPostStatus.POST_UNDER_REVIEW:
                return 'Under Review'
            case DraftPostStatus.POST_APPROVED:
                return 'Approved'
            case DraftPostStatus.POST_REJECTED:
                return 'Rejected'
            default:
                return status.replace(/_/g, ' ')
        }
    }

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage)
            clearBulkSelection()
        }
    }

    const filteredPosts = (postsData?.draft_posts || []).filter((post) => {
        if (selectedUserId && post.user.id !== selectedUserId) {
            return false
        }

        if (viewMode === DraftPostViewMode.FEEDBACK) {
            if (!post.admin_feedback && !post.user_feedback) {
                return false
            }
        }
        if (post.status === selectedStatus) return true
        if (
            viewMode === DraftPostViewMode.POSTS &&
            [
                DraftPostStatus.IDEA_APPROVED_WITH_RESEARCH,
                DraftPostStatus.IDEA_APPROVED_WITHOUT_RESEARCH,
            ].includes(post.status as DraftPostStatus) &&
            [
                DraftPostStatus.IDEA_APPROVED_WITH_RESEARCH,
                DraftPostStatus.IDEA_APPROVED_WITHOUT_RESEARCH,
            ].includes(selectedStatus as DraftPostStatus)
        ) {
            return true
        }
        return false
    })

    const handleToggleBulkSelect = (postId: string) => {
        toggleDraftPostSelection(postId)
    }

    const handleSelectSinglePost = (postId: string) => {
        setSelectedDraftPostId(postId)
        clearBulkSelection()
    }

    const handleBulkUpdate = async () => {
        if (selectedDraftPostIds.length > 0 && selectedBulkStatus) {
            bulkUpdateMutate(
                { ids: selectedDraftPostIds, status: selectedBulkStatus },
                {
                    onSuccess: () => {
                        setSelectedBulkStatus('')
                    },
                },
            )
        }
    }

    const groupedPosts = groupPostsBySource(filteredPosts)

    const sortedGroupEntries = Object.entries(groupedPosts).sort(([, postsA], [, postsB]) => {
        const dateA = postsA[0]?.source_info?.created_at
        const dateB = postsB[0]?.source_info?.created_at
        if (!dateA && !dateB) return 0
        if (!dateA) return 1
        if (!dateB) return -1
        return new Date(dateB).getTime() - new Date(dateA).getTime()
    })

    return (
        <div className="w-72 overflow-y-auto border-r border-gray-200 bg-white">
            <div className="p-4 border-b border-gray-200">
                <div className="flex space-x-4 mb-4">
                    <button
                        className={`px-4 py-2 rounded ${
                            viewMode === DraftPostViewMode.POSTS
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200'
                        }`}
                        onClick={() => {
                            setViewMode(
                                DraftPostViewMode.POSTS,
                            ) /* setCurrentPage(1); clearBulkSelection(); */ /* Moved logic to useEffect */
                        }}
                    >
                        Posts
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${
                            viewMode === DraftPostViewMode.IDEAS
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200'
                        }`}
                        onClick={() => {
                            setViewMode(
                                DraftPostViewMode.IDEAS,
                            ) /* setCurrentPage(1); clearBulkSelection(); */ /* Moved logic to useEffect */
                        }}
                    >
                        Ideas
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${
                            viewMode === DraftPostViewMode.FEEDBACK
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200'
                        }`}
                        onClick={() => {
                            setViewMode(
                                DraftPostViewMode.FEEDBACK,
                            ) /* setCurrentPage(1); clearBulkSelection(); */ /* Moved logic to useEffect */
                        }}
                    >
                        Feedback
                    </button>
                </div>

                {viewMode === DraftPostViewMode.IDEAS && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {ideaStatusFilters.map((filter) => (
                            <button
                                key={filter.value}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isFilterSelected(filter)
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                onClick={() => handleSelectFilter(filter)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                )}

                {viewMode === DraftPostViewMode.POSTS && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {postStatusFilters.map((filter) => (
                            <button
                                key={filter.value}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isFilterSelected(filter)
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                onClick={() => handleSelectFilter(filter)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                )}

                {viewMode === DraftPostViewMode.FEEDBACK && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {feedbackStatusFilters.map((filter) => (
                            <button
                                key={filter.value}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isFilterSelected(filter)
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                onClick={() => handleSelectFilter(filter)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                )}

                {selectedDraftPostIds.length > 0 && (
                    <div className="p-4 border-y border-gray-200 bg-blue-50 mt-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-blue-800">
                                Selected: {selectedDraftPostIds.length} post(s)
                            </span>
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800"
                                onClick={clearBulkSelection}
                            >
                                Clear Selection
                            </button>
                        </div>
                        <div className="flex items-center space-x-2">
                            <select
                                className={`flex-grow form-select border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    bulkUpdateLoading ? 'bg-gray-100' : ''
                                }`}
                                value={selectedBulkStatus}
                                onChange={(e) =>
                                    setSelectedBulkStatus(e.target.value as DraftPostStatus)
                                }
                                disabled={bulkUpdateLoading}
                            >
                                <option value="">Change status to...</option>
                                {(viewMode === DraftPostViewMode.IDEAS
                                    ? [
                                          {
                                              value: DraftPostStatus.IDEA_UNDER_REVIEW,
                                              label: 'Under Review',
                                          },
                                          {
                                              value: DraftPostStatus.IDEA_APPROVED_WITH_RESEARCH,
                                              label: 'Approved with Research',
                                          },
                                          {
                                              value: DraftPostStatus.IDEA_APPROVED_WITHOUT_RESEARCH,
                                              label: 'Approved without Research',
                                          },
                                          {
                                              value: DraftPostStatus.IDEA_REJECTED,
                                              label: 'Rejected',
                                          },
                                          {
                                              value: DraftPostStatus.PENDING_RETRY,
                                              label: 'Pending Retry',
                                          },
                                      ]
                                    : [
                                          {
                                              value: DraftPostStatus.POST_UNDER_REVIEW,
                                              label: 'Ready for Review',
                                          },
                                          {
                                              value: DraftPostStatus.POST_APPROVED,
                                              label: 'Approved',
                                          },
                                          {
                                              value: DraftPostStatus.POST_REJECTED,
                                              label: 'Rejected',
                                          },
                                      ]
                                ).map((status) => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="px-3 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:bg-gray-400"
                                onClick={handleBulkUpdate}
                                disabled={!selectedBulkStatus || bulkUpdateLoading}
                            >
                                {bulkUpdateLoading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                        {bulkUpdateIsError && (
                            <p className="text-xs text-red-500 mt-1">
                                Error: {bulkUpdateError?.message || 'Failed to update status'}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {postsLoading || postsFetching ? (
                <div className="flex justify-center p-8">
                    <p>Loading draft posts...</p>
                </div>
            ) : postsIsError ? (
                <div className="p-4 text-center text-red-500">
                    Error loading posts: {postsError?.message || 'Unknown error'}
                </div>
            ) : filteredPosts.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No posts match the criteria</div>
            ) : (
                <div>
                    {sortedGroupEntries.map(([meetingGroup, groupPosts]) => {
                        const firstPost = groupPosts[0]
                        const meetingDate = firstPost?.source_info
                            ? formatDate(firstPost.source_info.created_at)
                            : ''

                        return (
                            <div key={meetingGroup} className="mb-6">
                                <div className="px-4 py-2 bg-gray-100 border-y border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-800">
                                        {meetingGroup !== 'Other Content'
                                            ? `📅 ${firstPost?.source_info?.title}`
                                            : '📄 Other Content'}
                                    </h3>
                                    {meetingDate && (
                                        <p className="text-xs text-gray-500">{meetingDate}</p>
                                    )}
                                </div>
                                <ul className="divide-y divide-gray-100 bg-white">
                                    {groupPosts.map((post) => {
                                        const isSelectedForBulk = selectedDraftPostIds.includes(
                                            post.id,
                                        )
                                        const isSelectedForDetail = selectedDraftPostId === post.id
                                        return (
                                            <li
                                                key={post.id}
                                                className={`flex items-center px-4 py-3 cursor-pointer ${
                                                    isSelectedForBulk
                                                        ? 'bg-yellow-50'
                                                        : isSelectedForDetail
                                                        ? 'bg-blue-50'
                                                        : 'hover:bg-gray-50'
                                                } ${
                                                    post.status === DraftPostStatus.POST_APPROVED &&
                                                    post.published_at &&
                                                    post.user?.last_emailed_at &&
                                                    new Date(post.user.last_emailed_at) >
                                                        new Date(post.published_at)
                                                        ? 'opacity-50'
                                                        : ''
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    checked={isSelectedForBulk}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleToggleBulkSelect(post.id)
                                                    }}
                                                />
                                                <div
                                                    className="flex-grow"
                                                    onClick={() => handleSelectSinglePost(post.id)}
                                                >
                                                    <div className="text-sm font-medium text-gray-600 truncate">
                                                        {post.idea_title}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Status: {getStatusLabel(post.status)}
                                                    </div>
                                                </div>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        )
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div className="p-4 border-t border-gray-200 flex justify-between items-center">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || postsFetching || postsLoading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || postsFetching || postsLoading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    )
}

export default DraftPostList
